import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Skeleton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { CheckCircle, ErrorOutline } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import financeService from '../../services/financeService';
import { PeriodPreset, Statement, StatementKind, StatementQuery } from '../../types/finance';
import { useTimezone } from '../../hooks/useTimezone';
import SearchableSelect from '../../components/common/SearchableSelect';
import DateRangePicker, { DateRange } from '../../components/common/DateRangePicker';
import DownloadMenu from '../../components/common/DownloadMenu';
import StatementTable from '../../components/finance/statements/StatementTable';
import { format } from 'date-fns';
import { usePdfExport, useSchoolBranding } from '../../utils/pdf';
import StatementDocument from '../../components/finance/pdf/StatementDocument';

const TABS: Array<{ kind: StatementKind; label: string; hint: string }> = [
  { kind: 'profit-and-loss', label: 'Profit & loss', hint: 'What the school earned and spent over a period.' },
  { kind: 'balance-sheet', label: 'Balance sheet', hint: 'What the school owns and owes on a date.' },
  { kind: 'cash-flow', label: 'Cash flow', hint: 'Where cash came from and went, and how it reconciles to profit.' },
];

const PERIODS: Array<{ value: PeriodPreset | 'custom'; label: string }> = [
  { value: 'month', label: 'This month' },
  { value: 'lastMonth', label: 'Last month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'fiscalYear', label: 'Fiscal year to date' },
  { value: 'lastFiscalYear', label: 'Last fiscal year' },
  { value: 'custom', label: 'Custom dates' },
];

/**
 * Finance → Statements: profit & loss, balance sheet and cash flow, read
 * from the books. Periods are fiscal (the server resolves the presets).
 */
const StatementsPage: React.FC = () => {
  const { getToday } = useTimezone();
  const [params, setParams] = useSearchParams();
  const kind = (TABS.find(t => t.kind === params.get('tab'))?.kind ?? 'profit-and-loss') as StatementKind;
  const [period, setPeriod] = useState<PeriodPreset | 'custom'>('fiscalYear');
  const [custom, setCustom] = useState<DateRange>({ from: null, to: null });
  const [asOf, setAsOf] = useState(getToday());
  const [compare, setCompare] = useState<'' | 'previous' | 'lastYear'>('');
  const [statement, setStatement] = useState<Statement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const q: StatementQuery = useMemo(() => {
    if (kind === 'balance-sheet') return { asOf, compare: compare === 'lastYear' ? 'lastYear' : undefined };
    const range = period === 'custom' ? { from: custom.from ?? undefined, to: custom.to ?? undefined } : { period };
    return { ...range, compare: kind === 'profit-and-loss' && compare ? compare : undefined };
  }, [kind, asOf, period, custom, compare]);

  const ready = kind === 'balance-sheet' || period !== 'custom' || !!custom.from;

  const load = useCallback(async () => {
    if (!ready) return;
    setLoading(true);
    try {
      setStatement(await financeService.getStatement(kind, q));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not prepare the statement.');
    } finally {
      setLoading(false);
    }
  }, [kind, q, ready]);

  useEffect(() => { load(); }, [load]);

  const compareOptions = kind === 'balance-sheet'
    ? [{ value: 'lastYear', label: 'A year earlier' }]
    : [{ value: 'previous', label: 'Previous period' }, { value: 'lastYear', label: 'Same period last year' }];
  const shown = statement?.kind === kind ? statement : null;

  // The PDF is drawn here from the statement as the server computes it; the CSV comes from the server.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const exportAs = async (type: 'pdf' | 'csv') => {
    if (type === 'csv') return financeService.exportStatementCsv(kind, q);
    let title = '';
    await generatePdf(async () => {
      const s = await financeService.getStatement(kind, q);
      title = s.title;
      return <StatementDocument statement={s} branding={branding} generatedAt={new Date().toISOString()} />;
    }, () => `${title.replace(/[^A-Za-z]+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Statements</Typography>
          <Typography variant="body2" color="text.secondary">{TABS.find(t => t.kind === kind)!.hint}</Typography>
        </Box>
        <DownloadMenu onExport={exportAs} />
      </Stack>

      <Tabs
        value={kind}
        onChange={(_, v) => { const next = new URLSearchParams(params); next.set('tab', v); setParams(next, { replace: true }); }}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {TABS.map(t => <Tab key={t.kind} value={t.kind} label={t.label} sx={{ textTransform: 'none', fontWeight: 600 }} />)}
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          {kind === 'balance-sheet' ? (
            <TextField size="small" type="date" label="As at" value={asOf} onChange={e => setAsOf(e.target.value || getToday())}
              InputLabelProps={{ shrink: true }} sx={{ width: 200 }} />
          ) : (
            <>
              <SearchableSelect label="Period" value={period} onChange={v => setPeriod((v || 'fiscalYear') as PeriodPreset | 'custom')}
                options={PERIODS} minWidth={210} />
              {period === 'custom' && <DateRangePicker value={custom} onChange={setCustom} label="Dates" minWidth={240} />}
            </>
          )}
          {kind !== 'cash-flow' && (
            <SearchableSelect label="Compare with" value={compare} onChange={v => setCompare(v as typeof compare)}
              options={compareOptions} placeholder="No comparison" minWidth={220} />
          )}
          <Box sx={{ flex: 1 }} />
          {shown?.kind === 'balance-sheet' && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: shown.balanced ? 'success.dark' : 'error.dark' }}>
              {shown.balanced ? <CheckCircle fontSize="small" /> : <ErrorOutline fontSize="small" />}
              <Typography variant="body2" fontWeight={600}>{shown.balanced ? 'Balances' : 'Does not balance'}</Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
      {!ready
        ? <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>Choose the dates for the statement.</Alert>
        : loading && !shown
          ? <Skeleton variant="rounded" height={420} sx={{ borderRadius: 2 }} />
          : shown && <StatementTable statement={shown} />}
    </Box>
  );
};

export default StatementsPage;
